import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Flame, Timer, Gauge, Plus, Minus, Play, Download, Share2, MessageCircle } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { downloadAsset } from "@/lib/download";
import { shareProduct, shareOnWhatsApp } from "@/lib/share";
import { formatINR, priceOf } from "@/config";
import { ProductOptionsFields } from "./ProductOptionsFields";
import { cleanSizes, sizeSelectionError, sizeTotal } from "@/lib/productDetails";

export default function ProductDialog({ product, onClose }) {
  const { addItem, items, setDrawerOpen } = useCart();
  const [qty, setQty] = useState(1);
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [sizes, setSizes] = useState({});
  const [details, setDetails] = useState("");
  const [detailsTouched, setDetailsTouched] = useState(false);
  const shownDetails = detailsTouched ? details : items.find((item) => item.product.id === product?.id)?.details || "";
  const hasSizes = !!product?.size_options?.length;
  const selectedQty = hasSizes ? sizeTotal(sizes) : qty;
  const selectionError = product ? sizeSelectionError(product, sizes) : "";

  useEffect(() => {
    setQty(1);
    setActive(0);
    setPlaying(false);
    setDownloading(false);
    setSizes({});
    setDetails("");
    setDetailsTouched(false);
  }, [product]);

  const handleDownload = async () => {
    if (!product || downloading) return;
    setDownloading(true);
    await downloadAsset(`/products/${product.id}/download`, `${product.sku || "product"}-varsha.png`, "product image");
    setDownloading(false);
  };

  const gallery = (product?.images?.length ? product.images : [product?.image]).filter(Boolean);
  const videos = product?.videos?.filter(Boolean) || [];

  return (
    <AnimatePresence>
      {product && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-center justify-center bg-[#0A0B0E]/80 p-4 backdrop-blur-sm"
          onClick={onClose}
          data-testid="product-detail-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.96 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            data-lenis-prevent
            className="spotlight-frame relative grid max-h-[92dvh] w-full max-w-3xl overflow-y-auto rounded-3xl border border-white/10 bg-[#12141A] md:grid-cols-2"
          >
            <button
              onClick={onClose}
              data-testid="product-detail-close-btn"
              aria-label="Close"
              className="absolute right-3 top-3 z-20 flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-[#0A0B0E]/80 text-slate-300 backdrop-blur transition-colors hover:border-fuchsia-400 hover:text-fuchsia-300"
            >
              <X size={16} />
            </button>
            <div className="relative aspect-square overflow-hidden md:aspect-[4/5]">
              {product.is_live === false ? (
                <div data-testid="product-detail-coming-soon-panel" className="relative flex h-full w-full flex-col items-center justify-center overflow-hidden bg-[#0C0E14] md:sticky md:top-0 md:h-[30rem]">
                  <div className="absolute inset-0 opacity-[0.18] [background-image:repeating-linear-gradient(135deg,#22d3ee_0,#22d3ee_1px,transparent_1px,transparent_11px)]" />
                  <div className="absolute -left-12 top-1/2 h-52 w-52 -translate-y-1/2 rounded-full bg-fuchsia-600/25 blur-3xl" />
                  <div className="absolute -right-12 top-1/4 h-52 w-52 rounded-full bg-cyan-500/25 blur-3xl" />
                  <span className="relative font-display text-4xl font-bold uppercase leading-none tracking-tight text-transparent [background-image:linear-gradient(100deg,#22d3ee,#e879f9,#facc15)] [-webkit-background-clip:text] [background-clip:text]">
                    Coming
                  </span>
                  <span className="relative font-display text-4xl font-bold uppercase leading-none tracking-tight text-transparent [background-image:linear-gradient(100deg,#facc15,#e879f9,#22d3ee)] [-webkit-background-clip:text] [background-clip:text]">
                    Soon
                  </span>
                  {product.sku && (
                    <span className="relative mt-5 rounded-full border border-white/15 bg-[#0A0B0E]/70 px-4 py-1.5 font-mono text-xs uppercase tracking-[0.2em] text-cyan-300 backdrop-blur" data-testid="product-detail-code">
                      Code {product.sku}
                    </span>
                  )}
                  <span className="relative mt-3 font-display text-2xl font-bold text-slate-50">{formatINR(product.price)}</span>
                  <span className="relative mt-3 font-mono text-[9px] uppercase tracking-[0.25em] text-slate-500">Ask us for launch date</span>
                </div>
              ) : (
                <>
                  {playing && videos.length > 0 ? (
                    <video data-testid="product-detail-video" src={videos[0]} controls autoPlay playsInline className="h-full w-full bg-black object-contain" />
                  ) : (
                  <img data-testid="product-detail-active-image" src={gallery[active] || product.image || "/placeholder-product.svg"} alt={product.name} onError={(e) => { e.currentTarget.src = "/placeholder-product.svg"; }} className="h-full w-full object-contain" />
                  )}
                  {videos.length > 0 && !playing && (
                    <button
                      type="button"
                      data-testid="product-detail-play-btn"
                      onClick={() => setPlaying(true)}
                      className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-[#0A0B0E]/70 text-cyan-300 backdrop-blur transition-transform hover:scale-110"
                    >
                      <Play size={22} className="ml-1 fill-cyan-300" />
                    </button>
                  )}
                  {videos.length > 0 && playing && (
                    <button
                      type="button"
                      data-testid="product-detail-stop-video-btn"
                      onClick={() => setPlaying(false)}
                      className="absolute left-3 top-3 rounded-full bg-[#0A0B0E]/80 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-300 backdrop-blur hover:text-cyan-300"
                    >
                      Photos
                    </button>
                  )}
                  {gallery.length > 1 && !playing && (
                    <div data-testid="product-detail-thumbs" className="absolute bottom-3 left-3 right-3 flex gap-2 overflow-x-auto">
                      {gallery.map((src, i) => (
                        <button
                          key={src + i}
                          data-testid={`product-detail-thumb-${i}`}
                          onClick={() => setActive(i)}
                          className={`h-12 w-12 shrink-0 overflow-hidden rounded-lg border transition-colors ${i === active ? "border-cyan-400" : "border-white/20 hover:border-white/50"}`}
                        >
                          <img src={src} alt="" onError={(e) => { e.currentTarget.closest("button").style.display = "none"; }} className="h-full w-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="relative min-w-0 p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] sm:p-7 [overflow-wrap:anywhere]">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-fuchsia-400">
                {product.category}
                {product.sku && <span data-testid="product-detail-code-badge" className="ml-2 rounded-md border border-cyan-400/25 bg-cyan-400/10 px-2 py-0.5 text-cyan-300">Code {product.sku}</span>}
                {product.is_live === false && <span data-testid="product-detail-coming-soon" className="ml-2 rounded-full bg-yellow-300/15 px-2 py-0.5 text-yellow-300">Coming soon</span>}
                {product.is_live !== false && (
                  <span data-testid="product-detail-stock" className={`ml-2 rounded-full px-2 py-0.5 ${product.stock > 0 ? "bg-cyan-400/10 text-cyan-300" : "bg-red-500/15 text-red-400"}`}>
                    {product.stock > 0 ? "In stock" : "Sold out"}
                  </span>
                )}
              </div>
              <h3 className="mt-2 font-display text-xl font-bold sm:text-2xl">{product.name}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">{product.description}</p>

              <div className="mt-5 grid grid-cols-3 gap-2">
                {[
                  { icon: Flame, label: "Temp", value: product.press_temp, color: "text-yellow-300" },
                  { icon: Timer, label: "Time", value: product.press_time, color: "text-cyan-300" },
                  { icon: Gauge, label: "Pressure", value: product.press_pressure, color: "text-fuchsia-300" },
                ].map(({ icon: Icon, label, value, color }) => (
                  <div key={label} className="rounded-xl border border-white/10 bg-[#0A0B0E]/60 p-3 text-center">
                    <Icon size={15} className={`mx-auto ${color}`} />
                    <div className="mt-1.5 font-mono text-[9px] uppercase tracking-[0.15em] text-slate-500">{label}</div>
                    <div className="mt-0.5 font-mono text-[11px] font-medium text-slate-200">{value}</div>
                  </div>
                ))}
              </div>

              <div className="mt-6 flex items-center justify-between">
                <div>
                  {product.offer_price != null ? (
                    <div data-testid="product-detail-offer-price">
                      <div className="font-display text-2xl font-bold text-[#F5C45A]">
                        {formatINR(product.offer_price)}
                        <span className="ml-2 font-mono text-sm font-normal text-slate-500 line-through">{formatINR(product.price)}</span>
                      </div>
                      <div className="mt-0.5 font-mono text-[10px] uppercase tracking-wider text-[#F5C45A]">
                        {Math.round(product.offer_percent)}% off · {product.offer_title}
                      </div>
                    </div>
                  ) : (
                    <div className="font-display text-2xl font-bold">{formatINR(product.price)}</div>
                  )}
                  {product.bulk_price && (
                    <div className="font-mono text-[10px] uppercase tracking-wider text-yellow-300">
                      {formatINR(product.bulk_price)} at {product.bulk_min}+ units
                    </div>
                  )}
                </div>
                {!hasSizes && <div className="flex items-center gap-1.5 rounded-full border border-white/10 px-1.5 py-1">
                  <button data-testid="detail-qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="flex h-9 w-9 items-center justify-center text-slate-400 hover:text-cyan-300"><Minus size={14} /></button>
                  <span data-testid="detail-qty-value" className="w-6 text-center font-mono text-sm">{qty}</span>
                  <button data-testid="detail-qty-plus" onClick={() => setQty((q) => q + 1)} className="flex h-9 w-9 items-center justify-center text-slate-400 hover:text-cyan-300"><Plus size={14} /></button>
                </div>}
              </div>

              {product.is_live !== false && <div className="mt-5 border-t border-white/10 pt-5">
                <ProductOptionsFields product={product} sizes={sizes} details={shownDetails} onSizes={setSizes} onDetails={(value) => { setDetails(value); setDetailsTouched(true); }} prefix="product-detail" disabled={product.stock <= 0} />
                {selectionError && <p data-testid="product-detail-size-error" role="status" className="mt-3 text-xs text-amber-300">{selectionError}</p>}
                <p data-testid="product-detail-selected-total" className="mt-4 flex justify-between gap-3 text-sm"><span className="text-slate-400">{selectedQty} pieces</span><span className="font-semibold text-cyan-300">{formatINR(priceOf(product) * selectedQty)}</span></p>
              </div>}

              <button
                data-testid="detail-add-to-order-btn"
                disabled={product.is_live === false || product.stock <= 0 || !!selectionError}
                onClick={() => { addItem(product, selectedQty, { size_quantities: hasSizes ? cleanSizes(sizes) : {}, details: detailsTouched ? details : undefined }); onClose(); setDrawerOpen(true); }}
                className="mt-6 h-12 w-full rounded-full bg-gradient-to-r from-cyan-400 to-fuchsia-500 text-sm font-bold text-[#0A0B0E] transition-transform duration-300 hover:scale-[1.02] active:scale-95 disabled:opacity-30"
              >
                {product.is_live === false ? "Coming Soon" : `Add ${selectedQty} to Order`}
              </button>

              <div className="mt-3 flex gap-2">
                <button
                  data-testid="detail-download-btn"
                  onClick={handleDownload}
                  disabled={downloading}
                  className="flex h-11 flex-1 items-center justify-center gap-2 rounded-full border border-cyan-400/30 text-xs font-semibold uppercase tracking-[0.15em] text-cyan-300 transition-colors hover:bg-cyan-400/10 disabled:opacity-40"
                >
                  <Download size={15} /> {downloading ? "Preparing…" : "Download"}
                </button>
                <button
                  data-testid="detail-share-btn"
                  onClick={() => shareProduct(product)}
                  className="flex h-11 flex-1 items-center justify-center gap-2 rounded-full border border-fuchsia-400/30 text-xs font-semibold uppercase tracking-[0.15em] text-fuchsia-300 transition-colors hover:bg-fuchsia-400/10"
                >
                  <Share2 size={15} /> Share
                </button>
                <button
                  data-testid="detail-share-whatsapp-btn"
                  onClick={() => shareOnWhatsApp(product)}
                  title="Share on WhatsApp"
                  aria-label="Share on WhatsApp"
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/15 text-slate-300 transition-colors hover:border-green-400/50 hover:text-green-400"
                >
                  <MessageCircle size={15} />
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
